import * as pdfMake from 'pdfmake/build/pdfmake';
import { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import logoImg from '../../../assets/logo-black.png';

// Relatório de Atendimento 2.0 (layout aprovado): logo da EMPRESA no
// cabeçalho (cadastrado em Configurações › Parâmetros; cai para o padrão
// se vazio), faixa azul por pedido, FOTOS EMBUTIDAS em grade de até 3
// (vêm em base64 pelo detail?embedImages=1 — as com marca d'água de
// lat/long viram prova no documento), blocos de assinatura/recebedor/
// motorista e rodapé com paginação.

const AZUL = '#17457c';
const CINZA_CLARO = '#eef3f6';
const BORDA = '#dde4e6';
// largura útil da página A4 com as margens de 34pt
const LARGURA = 527;

const truncar = (text: string, max: number) =>
  text && text.length > max ? `${text.slice(0, max - 1)}…` : text || '';

// caixas com respiro interno e borda fina clara (visual do mockup)
const layoutCaixa = {
  hLineColor: () => BORDA,
  vLineColor: () => BORDA,
  hLineWidth: () => 0.8,
  vLineWidth: () => 0.8,
  paddingLeft: () => 10,
  paddingRight: () => 10,
  paddingTop: () => 6,
  paddingBottom: () => 6,
};

// faixa ARREDONDADA (canvas por baixo + texto por cima com margem negativa)
// — tabela do pdfmake não tem border-radius; é assim que o mockup vira PDF
const faixaArredondada = (
  altura: number,
  cor: string,
  conteudo: Content,
  margemTopo = 0
): Content => ({
  margin: [0, margemTopo, 0, 4] as [number, number, number, number],
  stack: [
    {
      canvas: [
        { type: 'rect', x: 0, y: 0, w: LARGURA, h: altura, r: 5, color: cor },
      ],
    },
    conteudo,
  ],
});

interface IReceiptActivity {
  tag: string;
  description: string;
  startHour: string;
  endHour: string;
  lat: string;
  lng: string;
  driverName: string;
  responsibleName: string;
  responseSignature: string | null;
  address: {
    street: string;
    number: string;
    neighborhood: string;
    city: string;
    state: string;
  };
  images: string[];
  // URLs originais das fotos (mesma ordem de images) — a foto embutida
  // vira link clicável para abrir/baixar em tamanho cheio
  imageLinks?: string[];
}

export interface ICustomerReceipt {
  customer: string;
  date: string;
  email: string | null;
  orders: { orderNumber: string; activities: IReceiptActivity[] }[];
}

const formatDateBr = (date: string) => {
  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year}`;
};

const rotulo = (text: string) => ({
  text,
  fontSize: 7,
  bold: true,
  color: AZUL,
  characterSpacing: 0.4,
});

// grade de fotos: 3 por linha; base64 embute, URL (download falhou no
// servidor) vira link; célula vazia completa a última linha
const gradeDeFotos = (images: string[], links?: string[]): Content => {
  const fotos = images || [];
  if (fotos.length === 0) {
    return {
      text: 'Sem imagens registradas nesta entrega',
      fontSize: 8,
      italics: true,
      color: '#888888',
      margin: [0, 6, 0, 0],
    };
  }

  const celulas = fotos.map((image, index) =>
    String(image).startsWith('data:image')
      ? {
          stack: [
            {
              image,
              fit: [150, 112] as [number, number],
              alignment: 'center',
              // clique abre a URL original em aba nova (baixar em alta)
              ...(links?.[index] ? { link: links[index] } : {}),
            },
            {
              text: `Foto ${index + 1}${links?.[index] ? ' · clique para abrir' : ''}`,
              fontSize: 7,
              color: '#888888',
              alignment: 'center',
              margin: [0, 2, 0, 0] as [number, number, number, number],
            },
          ],
          border: [false, false, false, false],
        }
      : {
          text: `Foto ${index + 1} (abrir)`,
          link: String(image),
          color: 'blue',
          decoration: 'underline' as const,
          fontSize: 8,
          alignment: 'center',
          margin: [0, 8, 0, 8] as [number, number, number, number],
          border: [false, false, false, false],
        }
  );

  const linhas: any[][] = [];
  for (let i = 0; i < celulas.length; i += 3) {
    const linha = celulas.slice(i, i + 3) as any[];
    while (linha.length < 3) {
      linha.push({ text: '', border: [false, false, false, false] });
    }
    linhas.push(linha);
  }

  return {
    margin: [0, 6, 0, 0],
    table: { widths: ['*', '*', '*'], body: linhas },
    layout: 'noBorders',
  };
};

const blocoDaEntrega = (activity: IReceiptActivity): Content => ({
  // unbreakable: a entrega que não couber desce INTEIRA para a próxima
  // página em vez de cortar foto/assinatura no meio
  unbreakable: activity.images.length <= 3,
  margin: [0, 0, 0, 14],
  stack: [
    faixaArredondada(22, CINZA_CLARO, {
      columns: [
        {
          text: truncar(activity.description || 'Equipamento', 64),
          fontSize: 9,
          bold: true,
        },
        {
          text: activity.tag || '',
          fontSize: 9,
          bold: true,
          color: AZUL,
          alignment: 'right',
          width: 'auto',
        },
      ],
      margin: [10, -16, 10, 8] as [number, number, number, number],
    }),
    {
      margin: [2, 4, 2, 0],
      columns: [
        {
          width: '*',
          stack: [
            rotulo('ENDEREÇO DA ENTREGA'),
            {
              text: `${activity.address.street || ''}, ${
                activity.address.number || ''
              } — ${activity.address.neighborhood || ''} · ${
                activity.address.city || ''
              } - ${activity.address.state || ''}`,
              fontSize: 8.5,
            },
          ],
        },
        {
          width: 55,
          stack: [
            rotulo('INÍCIO'),
            { text: activity.startHour || 'n/a', fontSize: 8.5, bold: true },
          ],
        },
        {
          width: 55,
          stack: [
            rotulo('TÉRMINO'),
            { text: activity.endHour || 'n/a', fontSize: 8.5, bold: true },
          ],
        },
      ],
      columnGap: 10,
    },
    gradeDeFotos(activity.images, activity.imageLinks),
    {
      margin: [0, 8, 0, 0],
      columns: [
        {
          width: '*',
          table: {
            widths: ['*'],
            heights: [8, 42],
            body: [
              [rotulo('ASSINATURA DO RECEBEDOR')],
              [
                activity.responseSignature &&
                String(activity.responseSignature).startsWith('data:image')
                  ? {
                      image: activity.responseSignature,
                      fit: [130, 40] as [number, number],
                      alignment: 'center',
                      border: [false, false, false, false],
                    }
                  : {
                      text: 'Não assinado',
                      fontSize: 8,
                      italics: true,
                      color: '#888888',
                      alignment: 'center',
                      margin: [0, 14, 0, 0] as [
                        number,
                        number,
                        number,
                        number,
                      ],
                    },
              ],
            ],
          },
          layout: layoutCaixa,
        },
        {
          width: '*',
          table: {
            widths: ['*'],
            body: [
              [rotulo('RECEBIDO POR')],
              [
                {
                  text: activity.responsibleName || 'n/a',
                  fontSize: 9,
                  bold: true,
                },
              ],
              [
                {
                  text: `às ${activity.endHour || '--'}`,
                  fontSize: 7.5,
                  color: '#888888',
                },
              ],
            ],
          },
          layout: {
            ...layoutCaixa,
            hLineWidth: (i: number) => (i === 0 || i === 3 ? 0.8 : 0),
            paddingTop: (i: number) => (i === 0 ? 6 : 1),
            paddingBottom: (i: number) => (i === 2 ? 6 : 1),
          },
        },
        {
          width: '*',
          table: {
            widths: ['*'],
            body: [
              [rotulo('MOTORISTA')],
              [
                {
                  text: activity.driverName || 'n/a',
                  fontSize: 9,
                  bold: true,
                },
              ],
              [{ text: ' ', fontSize: 7.5 }],
            ],
          },
          layout: {
            ...layoutCaixa,
            hLineWidth: (i: number) => (i === 0 || i === 3 ? 0.8 : 0),
            paddingTop: (i: number) => (i === 0 ? 6 : 1),
            paddingBottom: (i: number) => (i === 2 ? 6 : 1),
          },
        },
      ],
      columnGap: 10,
    },
  ],
});

const docDefinition = (
  logo: string,
  receipt: ICustomerReceipt
): TDocumentDefinitions => {
  const dateBr = formatDateBr(receipt.date);
  const agora = new Date();
  const emitido = `${agora.toLocaleDateString('pt-BR')} ${agora
    .toLocaleTimeString('pt-BR')
    .slice(0, 5)}`;
  const entregas = receipt.orders.reduce(
    (total, order) => total + order.activities.length,
    0
  );

  return {
    info: {
      title: 'Relatório de Atendimento',
      author: '4LogX',
      creationDate: agora,
    },
    pageMargins: [34, 34, 34, 42],
    footer: (currentPage, pageCount) => ({
      margin: [34, 10, 34, 0],
      columns: [
        {
          text: 'Documento gerado pela plataforma 4LogX',
          fontSize: 7.5,
          color: '#999999',
        },
        {
          text: `página ${currentPage} de ${pageCount}`,
          fontSize: 7.5,
          color: '#999999',
          alignment: 'right',
        },
      ],
    }),
    content: [
      {
        columns: [
          { image: logo, fit: [170, 54] },
          {
            stack: [
              {
                text: 'Relatório de Atendimento',
                bold: true,
                fontSize: 16,
                alignment: 'right',
              },
              {
                text: `emitido em ${emitido} · 4LogX`,
                fontSize: 7.5,
                color: '#888888',
                alignment: 'right',
                margin: [0, 3, 0, 0],
              },
            ],
            margin: [0, 10, 0, 0],
          },
        ],
      },
      {
        canvas: [
          {
            type: 'line',
            x1: 0,
            y1: 6,
            x2: 527,
            y2: 6,
            lineWidth: 2.5,
            lineColor: AZUL,
          },
        ],
        margin: [0, 0, 0, 10],
      },
      {
        table: {
          widths: ['*', 'auto', 'auto'],
          body: [
            [
              {
                stack: [
                  rotulo('CLIENTE'),
                  {
                    text: truncar(receipt.customer, 58),
                    fontSize: 10,
                    bold: true,
                    margin: [0, 2, 0, 0] as [number, number, number, number],
                  },
                ],
              },
              {
                stack: [
                  rotulo('DATA'),
                  {
                    text: dateBr,
                    fontSize: 10,
                    bold: true,
                    margin: [0, 2, 0, 0] as [number, number, number, number],
                  },
                ],
              },
              {
                stack: [
                  rotulo('ENTREGAS'),
                  {
                    text: String(entregas),
                    fontSize: 10,
                    bold: true,
                    alignment: 'center',
                    margin: [0, 2, 0, 0] as [number, number, number, number],
                  },
                ],
              },
            ],
          ],
        },
        layout: layoutCaixa,
        margin: [0, 0, 0, 8],
      },
      ...receipt.orders.flatMap((order): Content[] => [
        faixaArredondada(
          24,
          AZUL,
          {
            text: `Pedido ${order.orderNumber}`,
            color: '#ffffff',
            bold: true,
            fontSize: 10,
            margin: [10, -17, 10, 9] as [number, number, number, number],
          },
          10
        ),
        ...order.activities.map((activity) => blocoDaEntrega(activity)),
      ]),
    ],
    defaultStyle: { fontSize: 9 },
  };
};

export async function generateCustomerReceiptPDF(
  receipt: ICustomerReceipt,
  companyLogoBase64?: string | null
) {
  // logo da empresa cadastrado em Parâmetros; sem ele, o padrão do sistema
  let logo = companyLogoBase64 || '';
  if (!logo || !logo.startsWith('data:image')) {
    const imgBlob = await fetch(logoImg).then((res) => res.blob());
    logo = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result));
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(imgBlob);
    });
  }

  // download em vez de open(): popup depois do await é bloqueado no Safari
  const nome = `comprovante-${receipt.customer}-${receipt.date}`
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9-]+/g, '_');
  pdfMake.createPdf(docDefinition(logo, receipt)).download(`${nome}.pdf`);
}
